import { AxiosInstance } from "axios";
import { ApiRouteHelpers } from "../../../root/apiRoutes";
import { PaginatedDto } from "../dto/PaginatedDto";
import {
  PostmarkAttachmentDownloadDto,
  PostmarkBulkActionRequestDto,
  PostmarkConversationDetailResponseDto,
  PostmarkCreateDsrRequestDto,
  PostmarkCreateDsrResponseDto,
  PostmarkOrganizationConversationSummaryDto,
  PostmarkOrganizationMessageDto,
  PostmarkReplyRequestDto,
} from "../dto/PostmarkInboxDto";
import { OrganizationPublicId, UUIDString } from "../Types";

export class PostmarkInboxController {
  constructor(private axios: AxiosInstance) {}

  public getConversations = async (
    organizationId: OrganizationPublicId,
    params?: { page?: number; per?: number; q?: string; status?: string },
  ): Promise<PaginatedDto<PostmarkOrganizationConversationSummaryDto>> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.conversations(organizationId);
    const response = await this.axios.get<PaginatedDto<PostmarkOrganizationConversationSummaryDto>>(
      url,
      {
        params,
      },
    );
    return response.data;
  };

  public getConversationDetail = async (
    organizationId: OrganizationPublicId,
    conversationId: UUIDString,
    params?: { page?: number; per?: number },
  ): Promise<PostmarkConversationDetailResponseDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.conversation(
      organizationId,
      conversationId,
    );
    const response = await this.axios.get<PostmarkConversationDetailResponseDto>(url, { params });
    return response.data;
  };

  public reply = async (
    organizationId: OrganizationPublicId,
    conversationId: UUIDString,
    payload: PostmarkReplyRequestDto,
  ): Promise<PostmarkOrganizationMessageDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.reply(organizationId, conversationId);
    const response = await this.axios.post<PostmarkOrganizationMessageDto>(url, payload);
    return response.data;
  };

  public archive = async (
    organizationId: OrganizationPublicId,
    conversationId: UUIDString,
  ): Promise<PostmarkOrganizationConversationSummaryDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.archive(organizationId, conversationId);
    const response = await this.axios.post<PostmarkOrganizationConversationSummaryDto>(url);
    return response.data;
  };

  public unarchive = async (
    organizationId: OrganizationPublicId,
    conversationId: UUIDString,
  ): Promise<PostmarkOrganizationConversationSummaryDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.unarchive(
      organizationId,
      conversationId,
    );
    const response = await this.axios.post<PostmarkOrganizationConversationSummaryDto>(url);
    return response.data;
  };

  public markRead = async (
    organizationId: OrganizationPublicId,
    conversationId: UUIDString,
  ): Promise<PostmarkOrganizationConversationSummaryDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.markRead(organizationId, conversationId);
    const response = await this.axios.post<PostmarkOrganizationConversationSummaryDto>(url);
    return response.data;
  };

  public markUnread = async (
    organizationId: OrganizationPublicId,
    conversationId: UUIDString,
  ): Promise<PostmarkOrganizationConversationSummaryDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.markUnread(
      organizationId,
      conversationId,
    );
    const response = await this.axios.post<PostmarkOrganizationConversationSummaryDto>(url);
    return response.data;
  };

  public bulk = async (
    organizationId: OrganizationPublicId,
    payload: PostmarkBulkActionRequestDto,
  ): Promise<PostmarkOrganizationConversationSummaryDto[]> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.bulk(organizationId);
    const response = await this.axios.post<PostmarkOrganizationConversationSummaryDto[]>(
      url,
      payload,
    );
    return response.data;
  };

  public createDsr = async (
    organizationId: OrganizationPublicId,
    conversationId: UUIDString,
    payload: PostmarkCreateDsrRequestDto,
  ): Promise<PostmarkCreateDsrResponseDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.createDsr(
      organizationId,
      conversationId,
    );
    const response = await this.axios.post<PostmarkCreateDsrResponseDto>(url, payload);
    return response.data;
  };

  public downloadAttachment = async (
    organizationId: OrganizationPublicId,
    messageId: UUIDString,
    attachmentId: UUIDString,
  ): Promise<PostmarkAttachmentDownloadDto> => {
    const url = ApiRouteHelpers.organization.postmarkInbox.attachment(
      organizationId,
      messageId,
      attachmentId,
    );
    const response = await this.axios.get<PostmarkAttachmentDownloadDto>(url);
    return response.data;
  };
}
